import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChartLine, ArrowClockwise, CaretLeft, CaretRight, Table, CalendarBlank, X, NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";
import { NotaBadge, NotaDialog, NotasPanel } from "@/components/CashflowNotas";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
const fmtSign = (n) => {
  const v = Number(n) || 0;
  const abs = fmt(Math.abs(v));
  return v < 0 ? `-${abs}` : abs;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const DIAS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MESES_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function labelDia(iso) {
  const d = new Date(iso + "T12:00:00");
  const dia = DIAS_ES[d.getDay()];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return { dia, fecha: `${dd}/${mm}/${yyyy}` };
}

function shiftDate(iso, days) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function firstOfMonth(iso) {
  const d = new Date(iso + "T12:00:00");
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function daysInMonth(iso) {
  const d = new Date(iso + "T12:00:00");
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export default function CashFlow2Page() {
  // Persistir la vista elegida
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cf2_view_mode") || "tabla");
  const [fechaInicio, setFechaInicio] = useState(todayISO());
  const [dias, setDias] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Selección para vista calendario: { fecha, tipo: 'ingreso'|'egreso' }
  const [selection, setSelection] = useState(null);
  // Filtro de egresos: todo | cheques | proveedores (proveedores + servicios/insumos)
  const [filtroEgr, setFiltroEgr] = useState(() => localStorage.getItem("cf2_filtro_egr") || "todo");
  const changeFiltro = (f) => { localStorage.setItem("cf2_filtro_egr", f); setFiltroEgr(f); setSelection(null); };

  // Cuando estamos en calendario, forzamos el rango al mes completo del fechaInicio
  const effectiveInicio = viewMode === "calendario" ? firstOfMonth(fechaInicio) : fechaInicio;
  const effectiveDias = viewMode === "calendario" ? daysInMonth(fechaInicio) : dias;
  const effectiveFin = shiftDate(effectiveInicio, effectiveDias - 1);

  // Notas por día
  const [notas, setNotas] = useState([]);
  const [notaDlg, setNotaDlg] = useState(null); // { nota } | { fechaDefault }
  const loadNotas = useCallback(async () => {
    try {
      const { data } = await api.get("/cashflow-2/notas", { params: { desde: effectiveInicio, hasta: effectiveFin } });
      setNotas(data || []);
    } catch (e) {
      console.warn("notas cashflow:", e);
    }
  }, [effectiveInicio, effectiveFin]);
  useEffect(() => { loadNotas(); }, [loadNotas]);
  const notasPorFecha = useMemo(() => notas.reduce((acc, n) => { acc[n.fecha] = (acc[n.fecha] || 0) + 1; return acc; }, {}), [notas]);
  const abrirNota = (fecha) => setNotaDlg({ fechaDefault: fecha || data?.hoy || todayISO() });
  const borrarNota = async (n) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    try {
      await api.delete(`/cashflow-2/notas/${n.id}`);
      toast.success("Nota eliminada");
      loadNotas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la nota");
    }
  };
  const scrollNotas = () => document.querySelector('[data-testid="cf2-notas-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cashflow-2", { params: { fecha_inicio: effectiveInicio, dias: effectiveDias } });
      setData(data);
      setSelection(null); // reset detalle al cambiar de mes/período
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar cashflow");
    } finally {
      setLoading(false);
    }
  }, [effectiveInicio, effectiveDias]);
  useEffect(() => { load(); }, [load]);

  // Data con egresos filtrados (recalcula totales, caja diaria y neto)
  const dataF = useMemo(() => {
    if (!data || filtroEgr === "todo") return data;
    const keep = filtroEgr === "cheques" ? ["cheques"] : ["proveedores", "servicios_insumos"];
    const grupos = (data.egresos?.grupos || []).filter((g) => keep.includes(g.key));
    const n = (data.fechas || []).length;
    const total = Array.from({ length: n }, (_, i) => grupos.reduce((a, g) => a + (g.total_por_dia[i] || 0), 0));
    const detalles = grupos.flatMap((g) => g.detalles || []);
    const caja_diaria = Array.from({ length: n }, (_, i) => Math.round(((data.ingresos?.total_por_dia?.[i] || 0) - total[i]) * 100) / 100);
    const neto = data.hoy_idx >= 0 ? Math.round((caja_diaria[data.hoy_idx] + (data.saldo_cajas_total || 0)) * 100) / 100 : data.neto_del_dia;
    return { ...data, egresos: { ...data.egresos, grupos, detalles, total_por_dia: total }, caja_diaria, neto_del_dia: neto };
  }, [data, filtroEgr]);

  const changeView = (mode) => {
    localStorage.setItem("cf2_view_mode", mode);
    setViewMode(mode);
    setSelection(null);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ChartLine size={26} weight="duotone" className="text-emerald-700" />
          <div>
            <h1 className="text-2xl font-bold">Cashflow</h1>
          </div>
        </div>

        {/* Toggle Tabla/Calendario */}
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" role="tablist">
          <button
            data-testid="cf2-view-tabla"
            onClick={() => changeView("tabla")}
            className={`px-3 h-8 text-[12px] font-medium inline-flex items-center gap-1.5 ${viewMode === "tabla" ? "bg-slate-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <Table size={13} weight="bold" /> Tabla
          </button>
          <button
            data-testid="cf2-view-calendario"
            onClick={() => changeView("calendario")}
            className={`px-3 h-8 text-[12px] font-medium inline-flex items-center gap-1.5 border-l border-slate-300 ${viewMode === "calendario" ? "bg-slate-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <CalendarBlank size={13} weight="bold" /> Calendario
          </button>
        </div>

        {/* Filtro de egresos */}
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" role="tablist" data-testid="cf2-filtro-egresos">
          {[
            { k: "todo", l: "TODO" },
            { k: "cheques", l: "Cheques" },
            { k: "proveedores", l: "Proveedores / Servicios / Insumos" },
          ].map((f, i) => (
            <button
              key={f.k}
              data-testid={`cf2-filtro-${f.k}`}
              onClick={() => changeFiltro(f.k)}
              title={f.k === "todo" ? "Mostrar todos los egresos" : `Mostrar sólo egresos de ${f.l.toLowerCase()}`}
              className={`px-3 h-8 text-[12px] font-medium ${i > 0 ? "border-l border-slate-300" : ""} ${filtroEgr === f.k ? "bg-rose-700 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {/* Controles de fecha */}
        <div className="flex items-end gap-2">
          {viewMode === "tabla" ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setFechaInicio(shiftDate(fechaInicio, -dias))} title="Período anterior">
                <CaretLeft size={14} />
              </Button>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Desde</Label>
                <DateInput value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="h-8 w-40" data-testid="cf2-fecha-inicio" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Días</Label>
                <NumberInput min={1} max={31} value={dias} onChange={(e) => setDias(Number(e.target.value) || 8)} className="h-8 w-20" data-testid="cf2-dias" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFechaInicio(shiftDate(fechaInicio, dias))} title="Período siguiente">
                <CaretRight size={14} />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => {
                const d = new Date(fechaInicio + "T12:00:00");
                d.setMonth(d.getMonth() - 1);
                setFechaInicio(d.toISOString().slice(0, 10));
              }} title="Mes anterior" data-testid="cf2-cal-prev">
                <CaretLeft size={14} />
              </Button>
              <div className="text-[13px] font-semibold text-slate-700 min-w-[160px] text-center" data-testid="cf2-cal-title">
                {(() => {
                  const d = new Date(effectiveInicio + "T12:00:00");
                  return `${MESES_ES[d.getMonth()]} ${d.getFullYear()}`;
                })()}
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                const d = new Date(fechaInicio + "T12:00:00");
                d.setMonth(d.getMonth() + 1);
                setFechaInicio(d.toISOString().slice(0, 10));
              }} title="Mes siguiente" data-testid="cf2-cal-next">
                <CaretRight size={14} />
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => setFechaInicio(todayISO())} className="h-8">Hoy</Button>
          <Button variant="outline" size="sm" onClick={load} className="h-8 gap-1" data-testid="cf2-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Actualizar
          </Button>
        </div>
      </div>

      {viewMode === "tabla" ? (
        <TablaView data={dataF} notasPorFecha={notasPorFecha} onNota={abrirNota} onVerNotas={scrollNotas} />
      ) : (
        <CalendarioView data={dataF} selection={selection} setSelection={setSelection} notasPorFecha={notasPorFecha} onNota={abrirNota} onVerNotas={scrollNotas} />
      )}
      {filtroEgr !== "todo" && (
        <div className="text-[11px] text-rose-700 font-mono" data-testid="cf2-filtro-aviso">
          Egresos filtrados: {filtroEgr === "cheques" ? "sólo cheques" : "sólo proveedores / servicios / insumos"}. Caja diaria y Neto del día se recalculan con este filtro.
        </div>
      )}

      <NotasPanel
        notas={notas}
        hoy={data?.hoy}
        periodoLabel={`${labelDia(effectiveInicio).fecha} → ${labelDia(effectiveFin).fecha}`}
        onNueva={abrirNota}
        onEditar={(n) => setNotaDlg({ nota: n })}
        onBorrar={borrarNota}
      />
      {notaDlg && (
        <NotaDialog
          open
          nota={notaDlg.nota}
          fechaDefault={notaDlg.fechaDefault}
          onClose={() => setNotaDlg(null)}
          onSaved={loadNotas}
        />
      )}

      <div className="text-[11px] text-slate-500 font-mono">
        <b>Fuentes:</b> Ingresos = facturas de venta con saldo pendiente (por fecha de vto) + cheques recibidos aceptados (por fecha de cobro). Egresos = facturas de compra con saldo (por fecha de vto) + cheques emitidos aceptados (por fecha de cobro).
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- */
/* Vista TABLA (original)                                              */
/* ------------------------------------------------------------------- */
function TablaView({ data, notasPorFecha = {}, onNota, onVerNotas }) {
  const columnas = useMemo(() => (data?.fechas || []).map(labelDia), [data]);
  const cellMono = "px-2 py-1.5 text-right font-mono text-[13px] tabular-nums whitespace-nowrap";
  const headerCell = "px-2 py-2 text-center font-medium text-[11px] bg-slate-100 border border-slate-300";

  return (
    <div className="border rounded-lg bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="cf2-table">
        <thead>
          <tr>
            <th className={`${headerCell} text-left sticky left-0 z-10 min-w-[220px]`}>&nbsp;</th>
            {columnas.map((c, i) => {
              const f = data?.fechas?.[i];
              const nNotas = notasPorFecha[f] || 0;
              return (
                <th
                  key={i}
                  className={`${headerCell} min-w-[110px] group cursor-pointer select-none ${i === data?.hoy_idx ? "bg-amber-100 text-amber-900" : "hover:bg-slate-200/70"} ${nNotas ? "border-b-2 border-b-sky-500" : ""}`}
                  onClick={() => onNota?.(f)}
                  title="Clic para agregar una nota a este día"
                  data-testid={`cf2-col-${f}`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-[11px] font-semibold">{c.dia}</span>
                    <NotaBadge count={nNotas} onClick={onVerNotas} testId={`cf2-nota-badge-${f}`} />
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">{c.fecha}</div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          <tr className="bg-emerald-50 border-t border-emerald-200">
            <td className="px-3 py-1.5 font-semibold text-emerald-900 text-[13px] sticky left-0 bg-emerald-50 z-10 border-r">Ingresos</td>
            {(data?.ingresos.total_por_dia || columnas.map(() => 0)).map((v, i) => (
              <td key={i} className={`${cellMono} bg-emerald-50 font-semibold text-emerald-900 ${i === data?.hoy_idx ? "bg-emerald-100" : ""}`} data-testid={`cf2-ingresos-total-${i}`}>
                {v > 0 ? fmt(v) : ""}
              </td>
            ))}
          </tr>
          {(data?.ingresos.detalles || []).map((row, r) => (
            <tr key={`ing-${r}`} className="border-t border-slate-100 hover:bg-slate-50/70">
              <td className="pl-6 pr-3 py-1 text-[12px] text-slate-700 sticky left-0 bg-white z-10 truncate max-w-[220px]" title={row.concepto}>{row.concepto}</td>
              {row.por_dia.map((v, i) => (
                <td key={i} className={`${cellMono} text-emerald-800 ${i === data?.hoy_idx ? "bg-amber-50/40" : ""}`}>
                  {v > 0 ? fmt(v) : ""}
                </td>
              ))}
            </tr>
          ))}

          <tr className="bg-rose-50 border-t-4 border-rose-200">
            <td className="px-3 py-1.5 font-semibold text-rose-900 text-[13px] sticky left-0 bg-rose-50 z-10 border-r">Egresos</td>
            {(data?.egresos.total_por_dia || columnas.map(() => 0)).map((v, i) => (
              <td key={i} className={`${cellMono} bg-rose-50 font-semibold text-rose-900 ${i === data?.hoy_idx ? "bg-rose-100" : ""}`} data-testid={`cf2-egresos-total-${i}`}>
                {v > 0 ? fmt(v) : ""}
              </td>
            ))}
          </tr>
          {/* Egresos agrupados en 3 secciones (Cheques / Proveedores / Servicios-Insumos).
              Cada grupo trae su subtotal + filas hijas. Se plegaron sin acordeón para
              mantener consistencia visual con la sección de Ingresos. */}
          {(data?.egresos.grupos || []).map((grp) => (
            <Fragment key={`egr-grp-${grp.key}`}>
              <tr className="border-t border-rose-100 bg-rose-50/40">
                <td className="pl-4 pr-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-rose-800 sticky left-0 bg-rose-50/40 z-10 border-r" data-testid={`cf2-egresos-grp-${grp.key}`}>
                  {grp.nombre}
                </td>
                {(grp.total_por_dia || []).map((v, i) => (
                  <td key={i} className={`${cellMono} text-rose-700 font-semibold ${i === data?.hoy_idx ? "bg-amber-50/40" : ""}`} data-testid={`cf2-egresos-grp-${grp.key}-tot-${i}`}>
                    {v > 0 ? fmt(v) : ""}
                  </td>
                ))}
              </tr>
              {(grp.detalles || []).length === 0 ? (
                <tr className="border-t border-slate-100">
                  <td colSpan={1 + columnas.length} className="pl-8 pr-3 py-1 text-[11px] text-slate-400 italic sticky left-0 bg-white z-10">— sin egresos en {grp.nombre.toLowerCase()} —</td>
                </tr>
              ) : (grp.detalles || []).map((row, r) => (
                <tr key={`eg-${grp.key}-${r}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="pl-8 pr-3 py-1 text-[12px] text-slate-700 sticky left-0 bg-white z-10 truncate max-w-[220px]" title={row.concepto}>{row.concepto}</td>
                  {row.por_dia.map((v, i) => (
                    <td key={i} className={`${cellMono} text-rose-800 ${i === data?.hoy_idx ? "bg-amber-50/40" : ""}`}>
                      {v > 0 ? fmt(v) : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}

          <tr className="border-t-4 border-slate-300 bg-slate-50">
            <td className="px-3 py-2 font-semibold text-slate-800 sticky left-0 bg-slate-50 z-10 border-r">Caja diaria</td>
            {(data?.caja_diaria || columnas.map(() => 0)).map((v, i) => (
              <td key={i} className={`${cellMono} font-bold ${v > 0 ? "text-emerald-700" : v < 0 ? "text-rose-700" : "text-slate-500"} ${i === data?.hoy_idx ? "bg-amber-100" : ""}`} data-testid={`cf2-caja-diaria-${i}`}>
                {v !== 0 ? fmtSign(v) : ""}
              </td>
            ))}
          </tr>

          {/* Cajas discriminadas: hoy = disponible; días siguientes = acreditaciones pendientes */}
          <tr className="bg-slate-100 border-t border-slate-300">
            <td className="px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-slate-600 sticky left-0 bg-slate-100 z-10 border-r" colSpan={1}>
              Cajas <span className="normal-case tracking-normal font-normal text-slate-500">(hoy = disponible · después = se acredita)</span>
            </td>
            {columnas.map((_, i) => <td key={i} className="bg-slate-100" />)}
          </tr>
          {(data?.cajas || []).map((cj) => (
            <tr key={cj.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`cf2-caja-${cj.id}`}>
              <td className="pl-6 pr-3 py-1 text-[12px] text-slate-700 sticky left-0 bg-white z-10 border-r truncate max-w-[220px]" title={`${cj.nombre} · saldo ${fmt(cj.saldo)}${cj.pendiente_acreditacion > 0 ? ` · a acreditar ${fmt(cj.pendiente_acreditacion)}` : ""}`}>
                {cj.nombre}
                {cj.pendiente_acreditacion > 0 && <span className="ml-1 text-[10px] text-amber-700 font-mono">+{fmt(cj.pendiente_acreditacion)} a acreditar</span>}
              </td>
              {columnas.map((_, i) => {
                const acred = cj.acreditaciones_por_dia?.[i] || 0;
                const esHoy = i === data?.hoy_idx;
                return (
                  <td key={i} className={`${cellMono} ${esHoy ? "bg-amber-100 font-semibold text-slate-900" : acred > 0 ? "text-amber-700" : "text-slate-300"}`} data-testid={`cf2-caja-${cj.id}-${i}`}>
                    {esHoy ? fmt(cj.disponible_hoy) : acred > 0 ? `+${fmt(acred)}` : ""}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="bg-slate-100">
            <td className="px-3 py-1.5 text-slate-700 font-semibold sticky left-0 bg-slate-100 z-10 border-r">Total cajas disponible</td>
            {columnas.map((_, i) => {
              const acred = data?.acreditaciones_total_por_dia?.[i] || 0;
              return (
                <td key={i} className={`${cellMono} ${i === data?.hoy_idx ? "bg-amber-100 font-bold text-slate-900" : acred > 0 ? "text-amber-700 font-semibold" : ""}`} data-testid={`cf2-saldo-cajas-${i}`}>
                  {i === data?.hoy_idx ? fmt(data?.saldo_cajas_total || 0) : acred > 0 ? `+${fmt(acred)}` : ""}
                </td>
              );
            })}
          </tr>

          <tr className="bg-slate-200/70">
            <td className="px-3 py-1.5 font-semibold text-slate-900 sticky left-0 bg-slate-200/70 z-10 border-r">Neto del día</td>
            {columnas.map((_, i) => (
              <td key={i} className={`${cellMono} ${i === data?.hoy_idx ? "bg-amber-200 font-bold text-slate-900" : ""}`} data-testid={`cf2-neto-${i}`}>
                {i === data?.hoy_idx ? fmtSign(data?.neto_del_dia || 0) : ""}
              </td>
            ))}
          </tr>

          {/* Cobros del día — dinero que efectivamente entró ese día */}
          <tr className="bg-emerald-50/60">
            <td className="px-3 py-1.5 font-semibold text-emerald-800 sticky left-0 bg-emerald-50/60 z-10 border-r">Cobros del día</td>
            {columnas.map((_, i) => {
              const v = (data?.cobros_del_dia || [])[i] || 0;
              return (
                <td key={i} className={`${cellMono} ${v > 0 ? "text-emerald-700 font-semibold" : "text-slate-300"}`} data-testid={`cf2-cobros-${i}`}>
                  {v > 0 ? fmt(v) : ""}
                </td>
              );
            })}
          </tr>

          {/* Pagos del día — dinero que efectivamente salió ese día */}
          <tr className="bg-rose-50/60">
            <td className="px-3 py-1.5 font-semibold text-rose-800 sticky left-0 bg-rose-50/60 z-10 border-r">Pagos del día</td>
            {columnas.map((_, i) => {
              const v = (data?.pagos_del_dia || [])[i] || 0;
              return (
                <td key={i} className={`${cellMono} ${v > 0 ? "text-rose-700 font-semibold" : "text-slate-300"}`} data-testid={`cf2-pagos-${i}`}>
                  {v > 0 ? fmt(v) : ""}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------- */
/* Vista CALENDARIO                                                    */
/* ------------------------------------------------------------------- */
function CalendarioView({ data, selection, setSelection, notasPorFecha = {}, onNota, onVerNotas }) {
  // Construir grilla 7xN semanas comenzando LUNES
  const grid = useMemo(() => {
    if (!data?.fechas?.length) return [];
    const primeraFecha = data.fechas[0];
    const primerDia = new Date(primeraFecha + "T12:00:00");
    // Domingo=0, Lunes=1 ... Sábado=6 → convertimos a offset con lunes=0
    const dayOfWeek = (primerDia.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < dayOfWeek; i++) cells.push(null); // padding inicial
    data.fechas.forEach((f, idx) => cells.push({ fecha: f, idx }));
    while (cells.length % 7 !== 0) cells.push(null); // padding final
    // Partir en semanas
    const semanas = [];
    for (let i = 0; i < cells.length; i += 7) semanas.push(cells.slice(i, i + 7));
    return semanas;
  }, [data]);

  const detalleSeleccion = useMemo(() => {
    if (!selection || !data) return null;
    const idx = (data.fechas || []).indexOf(selection.fecha);
    if (idx < 0) return null;
    const src = selection.tipo === "ingreso" ? data.ingresos : data.egresos;
    const items = (src.detalles || [])
      .map((r) => ({ concepto: r.concepto, monto: r.por_dia[idx] || 0 }))
      .filter((r) => r.monto > 0)
      .sort((a, b) => b.monto - a.monto);
    const total = src.total_por_dia[idx] || 0;
    return { items, total };
  }, [selection, data]);

  return (
    <div className="space-y-4">
      {/* Grilla */}
      <div className="border rounded-lg bg-white overflow-hidden" data-testid="cf2-calendar">
        <div className="grid grid-cols-7 bg-slate-800 text-white text-[11px] font-semibold uppercase tracking-wider">
          {["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"].map((d) => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        {grid.map((semana, si) => (
          <div key={si} className="grid grid-cols-7 border-t border-slate-200">
            {semana.map((cell, ci) => {
              if (!cell) return <div key={ci} className="min-h-[110px] bg-slate-50/40 border-l first:border-l-0 border-slate-100" />;
              const ing = data.ingresos.total_por_dia[cell.idx] || 0;
              const egr = data.egresos.total_por_dia[cell.idx] || 0;
              const isToday = cell.idx === data.hoy_idx;
              const isPast = data.hoy_idx != null && cell.idx < data.hoy_idx;
              const day = new Date(cell.fecha + "T12:00:00").getDate();
              const isSel = selection && selection.fecha === cell.fecha;
              const nNotas = notasPorFecha[cell.fecha] || 0;
              return (
                <div
                  key={ci}
                  className={
                    "group min-h-[110px] border-l first:border-l-0 border-slate-100 p-1.5 flex flex-col relative " +
                    (isToday
                      ? "bg-amber-100 ring-2 ring-inset ring-amber-500 z-10 shadow-inner"
                      : isPast
                        ? "bg-slate-100/80 opacity-60"
                        : "") +
                    (isSel ? " ring-2 ring-inset ring-slate-500" : "") +
                    (nNotas ? " border-t-2 border-t-sky-500" : "")
                  }
                  data-testid={`cf2-cell-${cell.fecha}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={
                        "text-[13px] font-semibold " +
                        (isToday
                          ? "text-amber-900"
                          : isPast
                            ? "text-slate-400"
                            : "text-slate-700")
                      }
                    >
                      {day}
                    </span>
                    <div className="flex items-center gap-1">
                      <NotaBadge count={nNotas} onClick={onVerNotas} testId={`cf2-nota-badge-${cell.fecha}`} />
                      <button
                        type="button"
                        onClick={() => onNota?.(cell.fecha)}
                        title="Agregar nota"
                        className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 text-sky-700 hover:bg-sky-100"
                        data-testid={`cf2-nota-add-${cell.fecha}`}
                      >
                        <NotePencil size={13} weight="bold" />
                      </button>
                      {isToday && (
                        <span className="text-[9px] font-heading font-black uppercase tracking-widest text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded-sm">
                          HOY
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1">
                    {ing > 0 && (
                      <button
                        onClick={() => setSelection({ fecha: cell.fecha, tipo: "ingreso" })}
                        className={
                          "text-[11px] font-mono font-semibold rounded px-1.5 py-0.5 text-right transition " +
                          (isPast
                            ? "text-slate-500 bg-slate-200/70 hover:bg-slate-300/70"
                            : "text-emerald-800 bg-emerald-50 hover:bg-emerald-100") +
                          (isSel && selection.tipo === "ingreso" ? " ring-1 ring-emerald-500" : "")
                        }
                        data-testid={`cf2-cell-ing-${cell.fecha}`}
                      >
                        +{fmt(ing)}
                      </button>
                    )}
                    {egr > 0 && (
                      <button
                        onClick={() => setSelection({ fecha: cell.fecha, tipo: "egreso" })}
                        className={
                          "text-[11px] font-mono font-semibold rounded px-1.5 py-0.5 text-right transition " +
                          (isPast
                            ? "text-slate-500 bg-slate-200/70 hover:bg-slate-300/70"
                            : "text-rose-800 bg-rose-50 hover:bg-rose-100") +
                          (isSel && selection.tipo === "egreso" ? " ring-1 ring-rose-500" : "")
                        }
                        data-testid={`cf2-cell-egr-${cell.fecha}`}
                      >
                        -{fmt(egr)}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Detalle del día seleccionado */}
      {selection && detalleSeleccion && (
        <div className="border rounded-lg bg-white p-4" data-testid="cf2-detalle">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
                Detalle · {labelDia(selection.fecha).dia} {labelDia(selection.fecha).fecha}
              </div>
              <div className={`font-heading text-[18px] font-bold ${selection.tipo === "ingreso" ? "text-emerald-800" : "text-rose-800"}`}>
                {selection.tipo === "ingreso" ? "Ingresos" : "Egresos"} · {selection.tipo === "ingreso" ? "+" : "-"}{fmt(detalleSeleccion.total)}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelection(null)}
              className="h-8 w-8 p-0"
              data-testid="cf2-detalle-close"
              title="Cerrar detalle"
            >
              <X size={14} />
            </Button>
          </div>
          {detalleSeleccion.items.length === 0 ? (
            <div className="text-[13px] text-slate-500 italic py-4 text-center">Sin comprobantes que aporten a este día.</div>
          ) : (
            <div className="overflow-hidden border rounded-md">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-[10px] uppercase font-mono tracking-wider text-slate-600">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Concepto</th>
                    <th className="px-3 py-1.5 text-right w-40">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {detalleSeleccion.items.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5">{r.concepto}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${selection.tipo === "ingreso" ? "text-emerald-700" : "text-rose-700"}`}>
                        {selection.tipo === "ingreso" ? "+" : "-"}{fmt(r.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
